/**
 * DocumentPreviewBox — preview for file detail pages.
 *
 * Priority:
 *  1. processed_file_url — the corrected/flattened file (PDF or image)
 *  2. imageUrl (first_page_image_url) — pre-rendered PNG thumbnail
 *  3. Document icon fallback
 *
 * The processed_file_url may be a PDF or an image depending on the pipeline step
 * reached. We detect by extension and render accordingly.
 * AC-3.3
 */
import React, { JSX } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
  'tiff',
  'tif',
  'bmp',
]);

function isImageUrl(url: string): boolean {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXTENSIONS.has(ext);
}

interface DocumentPreviewBoxProps {
  filename: string;
  imageUrl: string | null;
  processedFileUrl?: string | null;
}

const previewImageSx = {
  width: '100%',
  maxWidth: '100%',
  maxHeight: 400,
  objectFit: 'contain' as const,
  display: 'block',
} as const;

const filenameSx = {
  fontSize: 13,
  textAlign: 'center' as const,
  wordBreak: 'break-all' as const,
  maxWidth: 280,
} as const;

const DocumentPreviewBox = ({
  filename,
  imageUrl,
  processedFileUrl,
}: DocumentPreviewBoxProps): JSX.Element => {
  const theme = useTheme();

  const containerSx = {
    bgcolor: theme.palette.grey[900],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '30vh',
    overflow: 'hidden',
  };

  const iconSx = {
    fontSize: 56,
    color: theme.palette.grey[400],
  };

  const filenameColour = { color: theme.palette.grey[400] };

  if (processedFileUrl) {
    // If the processed file is an image, render it directly
    if (isImageUrl(processedFileUrl)) {
      return (
        <Box sx={containerSx}>
          <Box
            component="img"
            src={processedFileUrl}
            alt={filename}
            sx={previewImageSx}
          />
          <Typography sx={{ ...filenameSx, ...filenameColour, py: 1 }}>
            {filename}
          </Typography>
        </Box>
      );
    }

    // PDF — embed via <object>
    const pdfSrc = `${processedFileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;
    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          height: '30vh',
          bgcolor: theme.palette.grey[900],
          overflow: 'hidden',
        }}
      >
        <Box
          component="object"
          data={pdfSrc}
          type="application/pdf"
          sx={{ width: '100%', height: '100%', display: 'block' }}
          aria-label={filename}
          data-testid="pdf-preview"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography sx={{ color: theme.palette.grey[400], fontSize: 13 }}>
              Preview not available
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  // Fall back to the pre-rendered first-page thumbnail
  return (
    <Box sx={containerSx}>
      {imageUrl ? (
        <>
          <Box
            component="img"
            src={imageUrl}
            alt={filename}
            sx={previewImageSx}
          />
          <Typography sx={{ ...filenameSx, ...filenameColour, py: 1 }}>
            {filename}
          </Typography>
        </>
      ) : (
        <Box
          sx={{
            py: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <InsertDriveFileIcon sx={iconSx} />
          <Typography sx={{ ...filenameSx, ...filenameColour }}>
            {filename}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DocumentPreviewBox;
